import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  verifySession,
  deliverOrchestratedDocument,
} from "../api/paymentService";

import {
  FiCheckCircle,
  FiMail,
  FiSend,
  FiFileText,
  FiShield,
  FiLock,
} from "react-icons/fi";

import SafeIcon from "../common/SafeIcon";
import { useToast } from "../context/ToastContext";
import useNDAForm from "../hooks/useNDAForm";
import { decrypt } from "../utils/crypto";

const SuccessPage = React.memo(function SuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const { addToast } = useToast();
  const { resetForm } = useNDAForm();

  const [status, setStatus] = useState("verifying");
  const [documentData, setDocumentData] = useState(null);
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [documentMeta, setDocumentMeta] = useState({ id: null, hash: null });
  const [email, setEmail] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);


  // Use a ref to hold the formData so it's accessible outside the effect
  const savedFormDataRef = useRef(null);

  const handleDownloadDocx = useCallback(async () => {
    try {
      if (!documentData) {
        addToast("No document data available for DOCX", "error");
        return;
      }

      const { Document, Packer, Paragraph, TextRun } = await import("docx");

      const paragraphs = [
        new Paragraph({
          children: [
            new TextRun({
              text: `${documentData.title}`,
              bold: true,
              size: 28,
            }),
          ],
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: `Effective Date: ${documentData.effectiveDate}`,
              size: 24,
            }),
          ],
        }),
        new Paragraph({ text: "" }),
      ];

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: paragraphs,
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "NDA.docx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      try { sessionStorage.removeItem("axim_nda_draft"); localStorage.removeItem("axim_nda_draft"); } catch(e) { console.warn("Failed to clean up draft from storage", e); }
      URL.revokeObjectURL(url);

      addToast("DOCX downloaded successfully", "success");
    } catch (err) {
      console.error("DOCX Error:", err);
      addToast("Failed to generate DOCX", "error");
    }
  }, [documentData, addToast]);

  const handleDownload = useCallback(() => {
    if (documentBlobUrl) {
      const a = document.createElement("a");
      a.href = documentBlobUrl;

      let filename = "NDA.pdf";
      if (savedFormDataRef.current && savedFormDataRef.current.partyName) {
        const sanitizedPartyName = savedFormDataRef.current.partyName.replace(/[^a-zA-Z0-9]/g, '_');
        const dateStr = new Date().toISOString().split('T')[0];
        filename = `AXiM_NDA_${sanitizedPartyName}_${dateStr}.pdf`;
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      try { sessionStorage.removeItem("axim_nda_draft"); localStorage.removeItem("axim_nda_draft"); } catch(e) { console.warn("Failed to clean up draft from storage", e); }
    } else {
      addToast("Document is not ready yet.", "error");
    }
  }, [documentBlobUrl, addToast]);

  const handleStartOver = useCallback(() => {
    resetForm();
    try {
      localStorage.clear();
      localStorage.removeItem("axim_nda_draft_state");
    } catch(e) { console.warn("localStorage.clear failed", e); }
    try { localStorage.removeItem("axim_delivery_email"); } catch(e) { console.warn("localStorage.removeItem failed", e); }
    setEmail("");
    navigate("/");
  }, [navigate, resetForm]);


  const handleCopyHash = useCallback(() => {
    if (documentMeta.hash) {
      navigator.clipboard.writeText(documentMeta.hash)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch(err => console.error("Failed to copy text: ", err));
    }
  }, [documentMeta.hash]);

  const handleSendEmail = useCallback(async () => {
    if (isSendingEmail) return;
    if (!email || !email.includes("@")) {
      addToast("Please enter a valid email address.", "error");
      return;
    }

    if (!savedFormDataRef.current) {
      addToast(
        "Document data is missing. Please try downloading instead.",
        "error",
      );
      return;
    }

    try {
      setIsSendingEmail(true);
      const response = await deliverOrchestratedDocument({
        templateId: "nda_v1",
        email,
        formData: documentData,
        session_id: sessionId,
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          addToast(
            "Your secure session has expired. Please download your document directly using the button above.",
            "error",
          );
          return;
        }
        throw new Error("Failed to send email");
      }

      addToast("Email sent successfully!", "success");
      setEmail("");
    } catch (err) {
      console.error("Email error:", err);
      addToast(
        "Failed to send email. Please try downloading instead.",
        "error",
      );
    } finally {
      setIsSendingEmail(false);
    }
  }, [email, addToast]);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let isMounted = true;

    const verify = async () => {
      try {
        // In a real app, verifySession would securely confirm the payment on the backend
        await verifySession(sessionId);

        if (!isMounted) return;

        // Rehydrate form data from localStorage
        let savedData = null;
        try {
          savedData = typeof localStorage !== "undefined" ? localStorage.getItem("axim_nda_draft") : null;
        } catch (e) {
          console.warn("Failed to access localStorage", e);
        }
        let formData = null;
        if (savedData) {
          try {
            const decrypted = decrypt(savedData);
            const parsed = JSON.parse(decrypted);
            formData = parsed.formData ? parsed.formData : parsed;
          } catch (err) {
            console.error("Failed to parse form data from localStorage", err);
          }
        }

        if (formData) {
          savedFormDataRef.current = formData;
          // Generate document using Edge API
          setStatus("generating");

          try {
            // Replace local generation with Edge API call
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);

            const response = await fetchWithTimeout("/api/generate-nda", {
              signal: controller.signal,
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ ...formData, sessionId }),
            });

            clearTimeout(timeoutId);
            if (!response.ok) {
                if (response.status === 502 || response.status === 503) {
                    throw new Error("Our servers are experiencing high traffic. Your document draft is saved locally. Please try generating again in a moment.");
                }
                throw new Error("Edge generation failed");
            }

            const docId = response.headers.get("X-Document-ID");
            const docHash = response.headers.get("X-Document-Hash");
            if (docId) setDocumentMeta({ id: docId, hash: docHash });

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setDocumentBlobUrl(url);
            setDocumentData(formData);
            setStatus("success");

            // Background Auto-Distribution to recipient
            if (formData.recipientEmail) {
              deliverOrchestratedDocument({
                templateId: "nda_v1",
                email: formData.email, // Original sender email
                recipientEmail: formData.recipientEmail,
                formData: formData,
                session_id: sessionId,
              }).catch(err => console.error("Failed to auto-distribute to recipient", err));
            }

            // Background telemetry
            fetchWithTimeout("/api/v1/telemetry/ingest", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                event: "document_generated",
                type: "nda",
                session_id: sessionId,
              }),
            }).catch((e) => console.error("Telemetry error:", e));

            // DataLayer sync
            if (window.dataLayer) {
              window.dataLayer.push({
                event: "purchase",
                document_type: "nda",
              });
            }
          } catch (genErr) {
            console.error("Generation failed:", genErr);
            if (genErr.name === 'AbortError') {
              console.error("Our servers are experiencing high traffic. Your document draft is saved locally. Please try generating again in a moment.", "error");
            } else if (genErr.message.includes('high traffic')) {
              console.error(genErr.message, "error");
            } else {
              console.error("Document generation failed. Please try again.", "error");
            }
            setStatus("error");
          }
        } else {
          setStatus("error");
          console.error("No form data found to generate the document");
        }
      } catch (err) {
        if (!isMounted) return;
        setStatus("error");
        console.error("Session verification failed:", err);
      }
    };

    verify();

    return () => {
      isMounted = false;
    };
  }, [sessionId]);

  if (status === "verifying") {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
        <div className="bg-white/5 border border-white/10 rounded-3xl p-8 text-center max-w-sm w-full shadow-2xl">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-axim-teal border-t-transparent mx-auto mb-4"></div>
          <h3 className="text-xl font-bold text-zinc-100 print:text-black mb-2">
            Verifying Payment
          </h3>
          <p className="text-zinc-400">
            Please wait while we secure your document...
          </p>
        </div>
      </div>
    );
  }

  if (status === "generating") {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
        <div className="bg-white/5 border border-white/10 rounded-3xl p-8 text-center max-w-sm w-full shadow-2xl">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-axim-teal border-t-transparent mx-auto mb-4"></div>
          <h3 className="text-xl font-bold text-zinc-100 mb-2">
            Generating PDF
          </h3>
          <p className="text-zinc-400">
            Please wait while your document is assembled at the Edge...
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
        <div className="bg-white/5 border border-white/10 rounded-3xl p-8 text-center max-w-sm w-full shadow-2xl">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-400 text-3xl">!</span>
          </div>
          <h3 className="text-xl font-bold text-zinc-100 mb-2">
            Verification Failed
          </h3>
          <p className="text-zinc-400 mb-6">
            We could not verify your purchase or find your draft. Please try
            again or contact support.
          </p>
          <button
            onClick={handleStartOver}
            className="bg-axim-teal text-black font-bold py-3 px-6 rounded-xl hover:bg-teal-400 transition"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full print:bg-white print:text-black">
      <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 text-center max-w-2xl mx-auto shadow-2xl animate-fade-in-up duration-500 ease-out print:shadow-none print:border-none print:bg-white mt-12">
        <div className="w-20 h-20 bg-axim-teal/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-axim-teal/30">
          <SafeIcon
            icon={FiCheckCircle}
            className="text-axim-teal drop-shadow-[0_0_8px_rgba(0,229,255,0.8)]"
            size={40}
          />
        </div>
        <h2 className="text-3xl font-bold text-zinc-100 mb-4">
          Payment Successful!
        </h2>
        <p className="text-zinc-300 print:text-black mb-8 text-lg">
          Thank you for your purchase. Your secure Non-Disclosure Agreement is
          ready.
        </p>

        {documentMeta.id && (
          <div className="bg-black/40 border border-axim-teal/30 rounded-xl p-4 mb-8 text-left max-w-md mx-auto relative overflow-hidden">
            <div className="absolute top-0 right-0 p-2 opacity-10 pointer-events-none">
              <SafeIcon icon={FiShield} size={64} />
            </div>
            <h4 className="text-axim-teal font-bold mb-2 flex items-center gap-2">
              <SafeIcon icon={FiShield} /> Certificate of Authenticity
            </h4>
            <div className="space-y-2 text-xs text-zinc-300 font-mono">
              <div className="flex justify-between border-b border-white/5 pb-1">
                <span className="text-zinc-500">Secure Trace ID:</span>
                <span className="text-axim-teal flex items-center gap-2">
                  {documentMeta.id}
                  <span className="inline-flex items-center gap-1 bg-axim-teal/20 text-axim-teal text-[10px] px-2 py-0.5 rounded-full border border-axim-teal/30 shadow-[0_0_10px_rgba(0,229,255,0.2)]">
                    <SafeIcon icon={FiLock} size={10} />
                    Cryptographically Sealed
                  </span>
                </span>
              </div>
              {documentMeta.hash && (
                <div className="flex justify-between border-b border-white/5 pb-1 items-center">
                  <span className="text-zinc-500">Document Hash:</span>
                  <div className="flex items-center gap-2">
                    <span
                      className="truncate max-w-[120px]"
                      title={documentMeta.hash}
                    >
                      {documentMeta.hash.substring(0, 16)}...
                    </span>
                    <button
                      onClick={handleCopyHash}
                      className="text-[10px] bg-axim-teal/20 text-axim-teal px-2 py-0.5 rounded border border-axim-teal/30 hover:bg-axim-teal/40 transition-colors"
                    >
                      {copySuccess ? 'Copied!' : 'Copy Seal'}
                    </button>
                  </div>
                </div>
              )}
              <div className="flex justify-between pt-1">
                <span className="text-zinc-500">Integrity:</span>
                <span className="text-green-400">Verified</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-4">
          <button
            onClick={() => navigate('/verify')}
            className="bg-axim-teal text-black font-bold py-3 px-8 rounded-xl hover:bg-teal-400 hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all flex items-center justify-center gap-2"
          >
            <SafeIcon icon={FiShield} />
            Verify This Document
          </button>
          <button
            onClick={() => window.print()}
            className="text-axim-teal font-medium py-3 px-8 rounded-xl hover:text-teal-400 transition-all flex items-center justify-center underline print:hidden"
          >
            Print Receipt
          </button>
        </div>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 print:hidden">
          <button
            onClick={handleDownload}
            className="bg-axim-teal text-black font-bold py-3 px-8 rounded-xl hover:bg-teal-400 hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all flex items-center justify-center"
          >
            Download PDF
          </button>
          <button
            onClick={handleDownloadDocx}
            className="bg-zinc-800 text-zinc-100 font-bold py-3 px-8 rounded-xl border border-zinc-700 hover:bg-zinc-700 transition-all flex items-center justify-center gap-2"
          >
            <SafeIcon icon={FiFileText} />
            Download Editable (.docx)
          </button>
          <button
            onClick={handleStartOver}
            className="bg-transparent border border-white/20 text-zinc-200 font-bold py-3 px-8 rounded-xl hover:bg-white/10 transition-all backdrop-blur-md"
          >
            Generate Another NDA
          </button>
        </div>

        {/* NEW: Phase 61 Auto-Email Delivery Confirmation Banner */}
        {(documentData?.recipientEmail || documentData?.email) && (
          <div className="bg-axim-teal/10 border border-axim-teal/20 backdrop-blur-md rounded-xl p-4 mb-8 text-center max-w-md mx-auto print:hidden flex items-center justify-center gap-3">
            <SafeIcon icon={FiMail} className="text-axim-teal flex-shrink-0" size={24} />
            <p className="text-sm text-axim-teal/90">
              A secure copy and receipt have been automatically sent to <strong className="text-axim-teal">{documentData.recipientEmail || documentData.email}</strong>.
            </p>
          </div>
        )}

        <div className="bg-black/30 border border-white/10 rounded-xl p-6 text-left max-w-md mx-auto print:hidden">
          <h3 className="text-lg font-bold text-zinc-100 mb-2 flex items-center gap-2">
            <SafeIcon icon={FiMail} className="text-axim-teal" />
            Email My Document
          </h3>
          <p className="text-sm text-zinc-400 mb-4">
            Send a copy of your generated NDA directly to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-black/50 border border-white/20 text-zinc-100 rounded-lg px-4 py-2 focus:outline-none focus:border-axim-teal focus:ring-1 focus:ring-axim-teal"
            />
            <button
              onClick={handleSendEmail}
              disabled={isSendingEmail || !email || !documentBlobUrl}
              className={`bg-zinc-800 text-zinc-100 font-medium py-2 px-6 rounded-lg hover:bg-zinc-700 transition flex items-center justify-center gap-2 ${
                isSendingEmail || !email || !documentBlobUrl
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              {isSendingEmail ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-zinc-300 border-t-transparent" /> : <SafeIcon icon={FiSend} size={14} />}
              {isSendingEmail ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
export default SuccessPage;
